import { IComponentsById, IComponentType, IScreenAnchorPositionType, ISpatialComponentUnion, ITuple3 } from '@r3f-component-data-structure';
import { Box3, Euler, Matrix4, Object3D, OrthographicCamera, Quaternion, Vector3 } from 'three';

export type IOverflowEdge = 'left' | 'right' | 'top' | 'bottom';

export interface IOverflowHit {
	entityId: string;
	anchor: IScreenAnchorPositionType;
	/** Fraction of the entity's screen area outside the viewport, 0..1. */
	clipped: number;
	edges: IOverflowEdge[];
}

/**
 * How much of an entity may hang off ONE side before it counts as a problem, as a fraction of the
 * entity's own size on that axis.
 *
 * Not a plain "any corner outside the frustum" test, deliberately: a background or scrim sized to bleed
 * past the edges is correct responsive design, and a bounds test flags it on every viewport, which would
 * leave the warning permanently on for well-built projects and teach people to ignore it. See the
 * two-sided rule in `measureAnchorGroupOverflow` for how bleed is told apart from displacement. The cost
 * of the threshold on top of that is that a sliver off one edge passes, which is the right way round.
 */
const CLIP_ENTER = 0.2;

/** Hysteresis: once flagged, an entity stays flagged until it is comfortably back inside. */
export const CLIP_LEAVE = 0.12;

const INNER_GROUP_PREFIX = 'transformGroup-inner_';
export const OUTER_GROUP_PREFIX = 'transformGroup-';

const NO_ANCHORED_EDGES: readonly IOverflowEdge[] = [];

/**
 * The frustum edges each anchor group is pinned to.
 *
 * An entity in one of these groups keeps a fixed offset from those edges on every viewport, so what hangs
 * off them is exactly what the author placed there and saw — a video tucked into the bottom-right corner
 * with its far side past the screen looks the same everywhere. That is a composition choice, and telling
 * the author it is "not anchored correctly" is both wrong and unactionable: the anchor is already the one
 * that makes it behave. Overhang past the OPPOSITE edges is still viewport-dependent and still counts —
 * a bottom-right entity can be pushed off the top or left of a narrow window.
 */
const ANCHORED_EDGES: Record<IScreenAnchorPositionType, readonly IOverflowEdge[]> = {
	[IScreenAnchorPositionType.leftTop]: ['left', 'top'],
	[IScreenAnchorPositionType.topMiddle]: ['top'],
	[IScreenAnchorPositionType.rightTop]: ['right', 'top'],
	[IScreenAnchorPositionType.rightMiddle]: ['right'],
	[IScreenAnchorPositionType.rightBottom]: ['right', 'bottom'],
	[IScreenAnchorPositionType.bottomMiddle]: ['bottom'],
	[IScreenAnchorPositionType.leftBottom]: ['left', 'bottom'],
	[IScreenAnchorPositionType.leftMiddle]: ['left'],
	[IScreenAnchorPositionType.center]: NO_ANCHORED_EDGES
};

const DEG_TO_RAD = Math.PI / 180;

// Scratch, allocated once. A sample runs at 4Hz over every screen entity in the scene and must not
// produce garbage.
const _box = new Box3();
const _geomBox = new Box3();
const _authored = new Matrix4();
const _anchorToNdc = new Matrix4();
const _cameraInverse = new Matrix4();
const _entityToNdc = new Matrix4();
const _identity = new Matrix4();
const _pos = new Vector3();
const _euler = new Euler();
const _quat = new Quaternion();
const _unitScale = new Vector3(1, 1, 1);
const _corner = new Vector3();
const _min = new Vector3();
const _max = new Vector3();
const _footprint = new Box3();
const _footprintMin = new Vector3();
const _footprintMax = new Vector3();

/**
 * One matrix per depth of the walk, so the recursion below composes without allocating. Grown on demand
 * and never shrunk; entity subtrees are a handful of levels deep.
 */
const _matrixStack: Matrix4[] = [];
const matrixAtDepth = (depth: number): Matrix4 => {
	while (_matrixStack.length <= depth) _matrixStack.push(new Matrix4());
	return _matrixStack[depth];
};

/**
 * Union the geometry of `node`'s subtree into `box`, in the frame of whatever `parentMatrix` is relative
 * to, with the preset-animation transforms taken back out.
 *
 * `transformGroup-inner_<id>` is identity in the markup and exists purely for animations to write
 * position/rotation/scale into, so its matrix is skipped rather than applied. The caller starts at the
 * outer group's CHILDREN, which drops that group's animated rotation and scale the same way. What is
 * left is the entity's own resting extent — see `resetEntityTransforms` in hoc/hocUtils.ts for the
 * matching list of what animations touch.
 *
 * Invisible nodes are skipped, which is what keeps hidden entities and the anchor groups of inactive
 * scenes out of the result. `Box3.setFromObject` would include both, which is why it is not used here.
 */
const unionStaticGeometry = (node: Object3D, parentMatrix: Matrix4, box: Box3, depth: number): void => {
	if (!node.visible) return;

	// The inner group is identity in the markup and exists only for animations to write into, so its
	// matrix is skipped rather than applied.
	let matrix = parentMatrix;
	if (!node.name.startsWith(INNER_GROUP_PREFIX)) {
		matrix = matrixAtDepth(depth).multiplyMatrices(parentMatrix, node.matrix);
	}

	const geometry = (node as { geometry?: { boundingBox: Box3 | null; computeBoundingBox: () => void } }).geometry;
	if (geometry) {
		if (!geometry.boundingBox) geometry.computeBoundingBox();
		if (geometry.boundingBox) box.union(_geomBox.copy(geometry.boundingBox).applyMatrix4(matrix));
	}

	for (const child of node.children) unionStaticGeometry(child, matrix, box, depth + 1);
};

/**
 * The entity types whose `scale` in the doc IS their footprint: a `CurvedEntity` plane of
 * `|scale[0]| x |scale[1]|` centred on the entity origin.
 *
 * Text, 3D text, models and emitters are excluded because their extent is emergent — troika lays text
 * out, a glTF brings its own bounds — and only the graph can answer for them.
 */
const PLANE_SHAPED: ReadonlySet<string> = new Set<string>([IComponentType.Video, IComponentType.Image, IComponentType.Button]);

/**
 * Union a plane-shaped entity's authored footprint into `box`, in the entity's own frame.
 *
 * Several of these types mount NO content geometry until an asset resolves — a video before playback
 * ([Video.tsx](../../../r3f-components/components/Video/Video.tsx) gates its plane on a thumbnail or a
 * ready video texture), a button before its SVG. What is left in the subtree meanwhile is the play
 * button and the spinner, both small and centred on the origin, so a box taken from geometry alone
 * reports a video hanging half off the screen as comfortably inside and the warning never fires.
 *
 * Unioning the doc's own footprint makes the measurement say the same thing whether or not the asset has
 * landed. It is a no-op once the plane is up: the plane is exactly this box.
 */
const unionAuthoredFootprint = (entity: ISpatialComponentUnion, box: Box3): void => {
	if (!PLANE_SHAPED.has(entity.type)) return;
	const [sx, sy] = (entity.scale ?? [0, 0, 0]) as ITuple3;
	const halfWidth = Math.abs(sx) / 2;
	const halfHeight = Math.abs(sy) / 2;
	if (halfWidth <= 0 || halfHeight <= 0) return;
	_footprintMin.set(-halfWidth, -halfHeight, 0);
	_footprintMax.set(halfWidth, halfHeight, 0);
	box.union(_footprint.set(_footprintMin, _footprintMax));
};

/** Whether every node from `node` up to (and including) `root` is visible. */
const isChainVisible = (node: Object3D, root: Object3D): boolean => {
	let current: Object3D | null = node;
	while (current) {
		if (!current.visible) return false;
		if (current === root) return true;
		current = current.parent;
	}
	return true;
};

interface IMeasureInput {
	/** The live anchor `<group>`. Its world matrix carries the anchor origin and normalization scale. */
	group: Object3D;
	anchor: IScreenAnchorPositionType;
	entityIds: string[];
	componentsById: IComponentsById;
	camera: OrthographicCamera;
	/** Entity ids currently flagged, so hysteresis can hold them until they are comfortably inside. */
	flagged: ReadonlySet<string>;
	/** Caller-owned, appended to. */
	out: IOverflowHit[];
}

/**
 * Append an `IOverflowHit` for every entity of this anchor group the current viewport substantially
 * clips.
 *
 * Works in normalized device coordinates rather than against `camera.left/right/top/bottom`, so it is
 * correct for both `screenRelativeScaleType`s, any zoom and any camera placement without needing to know
 * which is in play.
 */
export const measureAnchorGroupOverflow = ({ group, anchor, entityIds, componentsById, camera, flagged, out }: IMeasureInput): void => {
	if (!group.visible || !entityIds.length) return;

	// Both matrices are brought up to date here rather than trusted, so this does not depend on running
	// after the manual `gl.render` pass that normally refreshes them — the screen-relative camera is
	// `makeDefault={false}` and rendered at a `useFrame` priority above this one. Cheap at a 4Hz sample.
	group.updateWorldMatrix(true, true);
	camera.updateMatrixWorld();
	_cameraInverse.copy(camera.matrixWorld).invert();
	_anchorToNdc.multiplyMatrices(camera.projectionMatrix, _cameraInverse).multiply(group.matrixWorld);

	for (const entityId of entityIds) {
		const entity = componentsById?.[entityId] as ISpatialComponentUnion | undefined;
		if (!entity) continue;

		const outer = group.getObjectByName(OUTER_GROUP_PREFIX + entityId);
		if (!outer || !isChainVisible(outer, group)) continue;

		// Walked from the outer group's CHILDREN, not the group itself: its own matrix is the one
		// animations write rotation and scale into, and its authored part is re-applied below.
		_box.makeEmpty();
		for (const child of outer.children) unionStaticGeometry(child, _identity, _box, 0);
		unionAuthoredFootprint(entity, _box);
		if (_box.isEmpty()) continue;

		// The authored transform has to come from the doc: the outer group carries the authored position
		// and rotation as props AND is where animations write rotation and scale, so the graph cannot say
		// which is which. Scale is left at 1 — screen-relative scaling is applied below this group and is
		// already inside the box.
		// Defaulted rather than asserted: this runs inside `useFrame`, where a throw on a malformed doc
		// would take the whole experience down over a warning.
		const [rx, ry, rz] = (entity.rotation ?? [0, 0, 0]) as ITuple3;
		_pos.fromArray((entity.position ?? [0, 0, 0]) as ITuple3);
		_euler.set(rx * DEG_TO_RAD, ry * DEG_TO_RAD, rz * DEG_TO_RAD);
		_authored.compose(_pos, _quat.setFromEuler(_euler), _unitScale);

		_entityToNdc.multiplyMatrices(_anchorToNdc, _authored);

		_min.set(Infinity, Infinity, 0);
		_max.set(-Infinity, -Infinity, 0);
		for (let i = 0; i < 8; i++) {
			_corner
				.set(i & 1 ? _box.max.x : _box.min.x, i & 2 ? _box.max.y : _box.min.y, i & 4 ? _box.max.z : _box.min.z)
				.applyMatrix4(_entityToNdc);
			if (_corner.x < _min.x) _min.x = _corner.x;
			if (_corner.y < _min.y) _min.y = _corner.y;
			if (_corner.x > _max.x) _max.x = _corner.x;
			if (_corner.y > _max.y) _max.y = _corner.y;
		}

		const width = _max.x - _min.x;
		const height = _max.y - _min.y;
		if (width <= 1e-6 || height <= 1e-6) continue;

		const overhangLeft = Math.max(0, -1 - _min.x);
		const overhangRight = Math.max(0, _max.x - 1);
		const overhangBottom = Math.max(0, -1 - _min.y);
		const overhangTop = Math.max(0, _max.y - 1);

		// Overhanging BOTH opposite edges means the entity spans the viewport on that axis — a
		// full-bleed background, a scrim, a banner wider than the screen. That is correct responsive
		// design, not a mistake, so the axis contributes nothing. Only a ONE-sided overhang means the
		// entity has been pushed off, which is the thing the author cannot see and needs telling about.
		const spansX = overhangLeft > 0 && overhangRight > 0;
		const spansY = overhangTop > 0 && overhangBottom > 0;

		const anchoredEdges = ANCHORED_EDGES[anchor] ?? NO_ANCHORED_EDGES;
		const lostLeft = spansX || anchoredEdges.includes('left') ? 0 : overhangLeft;
		const lostRight = spansX || anchoredEdges.includes('right') ? 0 : overhangRight;
		const lostTop = spansY || anchoredEdges.includes('top') ? 0 : overhangTop;
		const lostBottom = spansY || anchoredEdges.includes('bottom') ? 0 : overhangBottom;

		const lostX = (lostLeft + lostRight) / width;
		const lostY = (lostTop + lostBottom) / height;
		const clipped = Math.max(lostX, lostY);

		const threshold = flagged.has(entityId) ? CLIP_LEAVE : CLIP_ENTER;
		if (clipped <= threshold) continue;

		// Only the edges that actually counted, so a host can name them without contradicting the score.
		const edges: IOverflowEdge[] = [];
		if (lostLeft > 0) edges.push('left');
		if (lostRight > 0) edges.push('right');
		if (lostTop > 0) edges.push('top');
		if (lostBottom > 0) edges.push('bottom');

		out.push({ entityId, anchor, clipped, edges });
	}
};
